import React from 'react';
import { formatEstimateFt, formatEstimateM3 } from '../utils/csomagStyleEstimate';

const BREAKDOWN_PLACEHOLDER = '------';

/**
 * Előzetes irányár modál (csomagszállítás-féle táblázat alapján).
 * @param {'box'|'aggregated'} props.volumeMode – box: térfogat a méretekből; aggregated: összesített m³, méret sor „—”
 * @param {number} props.estimateNet – nettó fuvar (minimum 20 000 Ft szabály már beépítve)
 */
export default function QuoteEstimateModal({
  open,
  onClose,
  distanceLoading,
  distanceError,
  distanceKm,
  weightKg,
  lengthCm,
  widthCm,
  heightCm,
  volumeM3,
  volumeMode = 'box',
  estimateNet,
}) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const firstWeight = Number(weightKg) || 0;
  const L = Number(lengthCm) || 0;
  const W = Number(widthCm) || 0;
  const H = Number(heightCm) || 0;
  const vol = Number(volumeM3) || 0;

  const sizeText =
    volumeMode === 'aggregated'
      ? '—'
      : L > 0 && W > 0 && H > 0
        ? `${L} × ${W} × ${H} cm`
        : '—';

  return (
    <div className="qf-estimate-backdrop" role="presentation" onClick={onClose}>
      <section
        className="qf-estimate-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qf-estimate-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="qf-estimate-close" aria-label="Bezárás" onClick={onClose}>
          ×
        </button>
        <div className="qf-estimate-head">
          <div className="qf-estimate-icon" aria-hidden="true">
            🧮
          </div>
          <div>
            <span className="qf-estimate-chip">Nem végleges</span>
            <h2 id="qf-estimate-title">Előzetes irányár</h2>
            <p>A megadott adatok alapján számolt, tájékoztató jellegű becslés.</p>
          </div>
        </div>

        <div className="qf-estimate-facts">
          <div className="qf-estimate-fact">
            <div className="qf-estimate-fact-label">Súly</div>
            <div className="qf-estimate-fact-value">{firstWeight > 0 ? `${firstWeight} kg` : '—'}</div>
          </div>
          <div className="qf-estimate-fact">
            <div className="qf-estimate-fact-label">Méret</div>
            <div className="qf-estimate-fact-value">{sizeText}</div>
          </div>
          <div className="qf-estimate-fact">
            <div className="qf-estimate-fact-label">Térfogat</div>
            <div className="qf-estimate-fact-value">{formatEstimateM3(vol)}</div>
          </div>
          <div className="qf-estimate-fact">
            <div className="qf-estimate-fact-label">Távolság</div>
            <div className="qf-estimate-fact-value">
              {distanceLoading && distanceKm <= 0 ? '…' : distanceKm > 0 ? `${distanceKm} km` : '—'}
            </div>
          </div>
        </div>

        <div className="qf-estimate-breakdown">
          <div className="qf-estimate-breakdown__row">
            <span>Távolság x Díjtétel</span>
            <strong className="qf-estimate-breakdown__num">
              {distanceLoading && distanceKm <= 0 ? '…' : distanceKm > 0 ? formatEstimateFt(estimateNet) : '—'}
            </strong>
          </div>
          <div className="qf-estimate-breakdown__row">
            <span>Rakodás</span>
            <strong className="qf-estimate-breakdown__num qf-estimate-breakdown__num--muted">
              {BREAKDOWN_PLACEHOLDER}
            </strong>
          </div>
          <div className="qf-estimate-breakdown__row">
            <span>Biztosítás</span>
            <strong className="qf-estimate-breakdown__num qf-estimate-breakdown__num--muted">
              {BREAKDOWN_PLACEHOLDER}
            </strong>
          </div>
        </div>

        <div className="qf-estimate-summary qf-estimate-summary--simple">
          <p className="qf-estimate-summary__single">
            <span className="qf-estimate-summary__label">Végösszeg</span>
            <span className="qf-estimate-summary__value">
              {distanceLoading && distanceKm <= 0 ? (
                <span className="qf-estimate-summary__amount">Számolás…</span>
              ) : distanceKm > 0 ? (
                <>
                  <span className="qf-estimate-summary__amount">{formatEstimateFt(estimateNet)}</span>
                  <span className="qf-estimate-summary__vat">+ áfa</span>
                </>
              ) : (
                <>
                  <span className="qf-estimate-summary__amount">—</span>
                  <span className="qf-estimate-summary__vat">+ áfa</span>
                </>
              )}
            </span>
          </p>
        </div>
        {!distanceLoading && distanceKm <= 0 && distanceError ? (
          <p className="qf-estimate-note qf-estimate-note--warn" role="status">
            {distanceError} Az irányárhoz szükség van a vezetési távolságra.
          </p>
        ) : null}

        <p className="qf-estimate-note">
          Ez az összeg nem minősül végleges ajánlatnak. A pontos árat kollégánk visszaigazolja.
        </p>

        <div className="qf-estimate-actions">
          <button type="button" className="secondary-btn" onClick={onClose}>
            Bezárás
          </button>
          <button type="button" className="primary-btn" onClick={onClose}>
            Rendben
          </button>
        </div>
      </section>
    </div>
  );
}
